import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import constants from '@/constants'
import { QUESTION_STATUS } from '@/constants/marketBackendEnums'
import { useActiveCurrency } from '@/hooks'
import { selectActiveMarketType } from '@/store/app'
import { fetchMarketById, selectMarketById, selectMarketStatusById } from '@/store/markets'
import { MIDTERMS_SENATE_MARKET_ID, MIDTERMS_USE_MOCK, MOCK_SENATE_MARKET } from '@/constants/midterms'
import { outcomeProbability } from '@/features/brazil-elections/utils'
import { getPositionLabels } from '@/features/market/utils/positionLabels'
import { deriveMarketUi } from '@/features/market/utils/marketType'
import { PARTY_COLORS, PARTY_LABEL_COLORS } from '@/features/midterm-map/midtermRaces'

/**
 * Matched against outcome titles rather than pinned to outcome ids, so the
 * panel survives the outcomes being renamed or reordered. Democrats keep the
 * left side in chamber-seating order, the same as the hemicycle behind them.
 */
/* `color` draws the fills, `labelColor` is the same hue lifted far enough to
   read as small type on the near-black ground. */
const PARTIES = [
  { party: 'democratic', pattern: /democrat/i, color: PARTY_COLORS.democratic, labelColor: PARTY_LABEL_COLORS.democratic },
  { party: 'republican', pattern: /republican/i, color: PARTY_COLORS.republican, labelColor: PARTY_LABEL_COLORS.republican },
]

/**
 * The two party sides, or null while they cannot be built. Two market shapes
 * are understood (see docs/MARKET_UI_TYPES.md):
 *
 * - Multi-outcome: each party is a physical outcome, matched by title.
 * - Mono custom (Type A): one physical outcome whose long/short position
 *   labels name the parties. The outcome's price quotes the long side, so the
 *   short side is its complement.
 */
const buildSides = (market, currencyId) => {
  const outcomes = Array.isArray(market?.outcomes) ? market.outcomes : []

  const fromOutcomes = PARTIES.map(({ pattern, ...side }) => {
    const outcome = outcomes.find(candidate => !candidate?.disabled && pattern.test(String(candidate?.title)))
    if (!outcome) return null
    return { ...side, label: outcome.title, probability: outcomeProbability(outcome, market, currencyId) }
  }).filter(Boolean)
  if (fromOutcomes.length === 2) return fromOutcomes

  /* The app's one market classifier: only a mono-custom market (Type A) has
     party-named positions. Mono yes/no and up/down never do. */
  const ui = deriveMarketUi(market)
  const outcome = ui.isMono && ui.needsVirtualOutcomes && !outcomes[0]?.disabled ? outcomes[0] : null
  if (!outcome) return null
  /* The shared resolver: long/short labels off the outcome, with the app's
     usual fallbacks. A fallback of Yes/No matches neither party and the
     banner stays down rather than guessing which side is which. */
  const { longLabel, shortLabel } = getPositionLabels(outcome, market)
  /* Null when the market has no quote yet; formatPercent prints it as a dash.
     The complement is clamped so a quote outside 0..1 cannot hand the bar a
     negative width. */
  const longProbability = outcomeProbability(outcome, market, currencyId)
  const shortProbability = longProbability == null ? null : Math.min(1, Math.max(0, 1 - longProbability))
  const fromPositions = PARTIES.map(({ pattern, ...side }) => {
    const isLong = pattern.test(String(longLabel))
    const isShort = !isLong && pattern.test(String(shortLabel))
    if (!isLong && !isShort) return null
    return {
      ...side,
      label: isLong ? longLabel : shortLabel,
      probability: isLong ? longProbability : shortProbability,
    }
  }).filter(Boolean)
  return fromPositions.length === 2 ? fromPositions : null
}

/**
 * The Senate-control market's two party outcomes for the home banner.
 * Three states, so the banner never shimmers forever:
 *
 * - `loading`: the detail fetch has not settled and the board cannot be built
 *   yet — the caller shows the skeleton.
 * - `hidden`: the fetch settled (or errored) and there is no open market with
 *   both party sides — the caller drops the whole banner.
 * - otherwise `sides` holds the pair and the board renders.
 *
 * Off production the snapshot in `constants/midterms` serves instead: the id
 * points at an unrelated market there.
 */
export default function useSenateControl() {
  const dispatch = useDispatch()
  const marketType = useSelector(selectActiveMarketType)
  const currencyId = useActiveCurrency().id
  const id = MIDTERMS_SENATE_MARKET_ID
  const stored = useSelector(selectMarketById(id))
  const status = useSelector(selectMarketStatusById(id))

  /* Keyed on the pool status, not on `stored`: a tag or category list fetch
     merges the market into the pool without outcomes and without a status, and
     only the detail fetch settles the board one way or the other. */
  useEffect(() => {
    if (MIDTERMS_USE_MOCK || (status && status !== constants.IDLE)) return
    dispatch(fetchMarketById(id, marketType)).catch?.(() => {})
  }, [dispatch, id, marketType, status])

  const market = MIDTERMS_USE_MOCK ? MOCK_SENATE_MARKET : stored
  const pair = buildSides(market, currencyId)

  /* The one source for the blue/red split, shared by the scoreboard's bar and
     the backdrop's seam so the two can never drift apart. Balanced while the
     market is loading or unquoted. */
  let share = 50
  if (pair && pair[0].probability != null && pair[1].probability != null) {
    const total = pair[0].probability + pair[1].probability
    if (total > 0) share = (pair[0].probability / total) * 100
  }

  const settled = MIDTERMS_USE_MOCK || status === constants.COMPLETE || status === constants.ERROR
  const isOpen = MIDTERMS_USE_MOCK || market?.status === QUESTION_STATUS.OPEN

  return {
    market,
    sides: pair,
    share,
    /* The detail fetch is still out and nothing usable is stored yet. */
    loading: !settled && !pair,
    /* The fetch settled and there is nothing to put on the board: it errored
       with nothing stored, the outcomes carry no matchable party pair, or the
       market is no longer open. The caller drops the whole banner. */
    hidden: settled && (!pair || !isOpen),
    /* Off production the snapshot's id points at an unrelated market, so the
       board must not link anywhere. */
    isLive: !MIDTERMS_USE_MOCK,
  }
}
